import { useEffect } from "react";

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: object | object[];
}

/**
 * 在每个页面组件中调用，用于更新 document 的 title / description / canonical，
 * 并注入页面级的 Schema.org JSON-LD 结构化数据。
 */
export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    document.title = meta.title;

    const setMeta = (attr: "name" | "property", key: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("name", "description", meta.description);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);

    if (meta.canonical) {
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", meta.canonical);
    }

    if (meta.jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-meta", "true");
      script.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
      return () => {
        document.head.querySelectorAll('script[data-page-meta="true"]').forEach((s) => s.remove());
      };
    }
    return undefined;
  }, [meta.title, meta.description, meta.canonical]);
}